import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DialogService } from "@app/directives/_layout/v-dialog/v-dialog.service";
import { IReseller } from "@app/main/modules/foundation/_models/IReseller";
import { FoundationAccountService } from "@app/main/modules/foundation/_services/account.service.ajs";
import { IAccount } from "@app/services/account.service";
import { ColourService } from "@app/services/colour.service";
import { CurrentUserService } from "@app/services/currentUser.service";
import { lastValueFrom, Observable } from "rxjs";
import { NPSRaterComponent } from "./nps-rater.component";

export interface INPSEntry {

    PlatformId: string;
    CustomerId: string;
    UserId: string;
    UserDescription: string;
    Score: number;
    Comments: string;
    DateTime?: string;

    // ---

    customer?: IAccount;
    color?: string;
    scoreLabel?: string;
}

export interface ICalculatedNPSScore {
    score?: number;

    promoter?: INPSCalculatedMetrics;
    passive?: INPSCalculatedMetrics;
    detractor?: INPSCalculatedMetrics;
}

export interface INPSCalculatedScore extends INPSCalculatedMetrics {
    score: number;
}

export interface INPSCalculatedMetrics {
    count: number;
    percentage: number;
    color: string;
}

export interface INPSAggregatedScore {
    description: string;

    ratings: INPSEntry[];
    score: ICalculatedNPSScore;
}

export interface IGetRatingsOptions {
    platformId?: string;
    customerId?: string;
    userId?: string;
    start?: Date;
    end?: Date;
    populateCustomers?: boolean;
    hideInternal?: boolean;
    hideCustomers?: boolean;
}

@Injectable()
export class NPSService {
    constructor(
        private readonly currentUserService: CurrentUserService,
        private readonly vDialogService: DialogService,
        private readonly http: HttpClient,
        private readonly colourService: ColourService,
        private readonly foundationAccountService: FoundationAccountService) {
    }

    showNPSRater(): Promise<INPSEntry> {

        const p = new Promise<INPSEntry>(resolve => {
            const ref = this.vDialogService.open<INPSEntry>(NPSRaterComponent, null);

            ref.afterClosed$.subscribe(res => {

                resolve(res.data);
            });
        });

        return p;
    }

    getLatestRating(options?: IGetRatingsOptions): Observable<INPSEntry> {

        const params = this.createHttpParams(options);

        var response = this.http.get<INPSEntry>('/api/nps/latest', { params: params });

        return response;
    }

    submitRating(entry: INPSEntry): Observable<any> {

        const headers = { 'content-type': 'application/json' }
        const body = JSON.stringify(entry);

        var response = this.http.post('/api/nps/submit', body, { 'headers': headers });

        return response;
    }

    getRatings(options?: IGetRatingsOptions): Promise<INPSEntry[]> {
        const params = this.createHttpParams(options);

        var npsEntries = lastValueFrom(this.http.get<INPSEntry[]>("/api/nps/get", { params: params }));
        if (!options.populateCustomers) {
            return npsEntries;
        }

        const customerPromise = this.foundationAccountService.getCustomerAccounts() as Promise<IAccount[]>;
        const resellersPromise = this.foundationAccountService.getResellers() as Promise<IReseller[]>;

        return Promise.all([npsEntries, customerPromise, resellersPromise]).then(([npsEntries, accounts, resellers]) => {
            npsEntries = this.filterRatingsByReseller(npsEntries, accounts);

            accounts.forEach(account => {
                account.resellerName = this.foundationAccountService.getResellerName(resellers, account.ResellerId);
            });

            var accountMap = new Map(accounts.map(account => [account.Id, account]))

            npsEntries.forEach(entry => {
                entry.customer = accountMap.get(Number(entry.CustomerId));
            });

            if (options?.hideInternal) {
                npsEntries = npsEntries.filter(entry => {
                    return entry.CustomerId !== "37" && entry.CustomerId !== "30442";
                })
            }

            if (options?.hideCustomers) {
                npsEntries = npsEntries.filter(entry => {
                    return entry.CustomerId == "37" || entry.CustomerId == "30442";
                })
            }

            return npsEntries;
        });
    }

    filterRatingsByReseller(ratings: INPSEntry[], accounts: IAccount[]): INPSEntry[] {

        const user = this.currentUserService.getCurrentUserFromCache();
        if (!user.IsReseller) {
            return ratings;
        }

        return ratings.filter(r => {
            var account = accounts.find(account => account.Id.toString() == r.CustomerId);
            if (!account) {
                return false;
            }

            if (!user.IsReseller) {
                return true;
            }

            return user.ResellerIds?.some(r => r === account.ResellerId) ?? false;
        });
    }

    prepareNPSEntriesForDisplay(entries: INPSEntry[]) {
        entries.forEach(e => {
            e.color = this.getColor(e.Score);

            switch (true) {
                case (e.Score >= 9):
                    e.scoreLabel = "Promoter";
                    break;

                case (e.Score >= 7):
                    e.scoreLabel = "Passive";
                    break;

                default:
                    e.scoreLabel = "Detractor";
                    break;
            }
        })
    }

    calculateNPSScore(entries: INPSEntry[]): ICalculatedNPSScore {

        var score: ICalculatedNPSScore = {};

        var detractors = entries.filter(e => this.isDetractor(e)),
            promoters = entries.filter(e => this.isPromoter(e));

        score.promoter = this.createCalculatedMetrics(promoters.length, entries.length, this.getColor(10));
        score.passive = this.createCalculatedMetrics(entries.length - detractors.length - promoters.length, entries.length, this.getColor(7));
        score.detractor = this.createCalculatedMetrics(detractors.length, entries.length, this.getColor(0));

        score.score = Math.round(score.promoter.percentage - score.detractor.percentage);

        return score;
    }

    isPromoter(entry: INPSEntry) {
        return entry.Score >= 9;
    }

    isDetractor(entry: INPSEntry) {
        return entry.Score <= 6;
    }

    calculateScoreHistogram(entries: INPSEntry[]): INPSCalculatedScore[] {

        var scoreHistogram: INPSCalculatedScore[] = [];

        // Create bins
        for (var i = 0; i <= 10; i++) {
            scoreHistogram.push({
                color: this.getColor(i),
                count: 0,
                percentage: 0,
                score: i
            });
        }

        // Populate
        var highestCount = 0;
        entries.forEach(entry => {
            var matchingHistogram = scoreHistogram[entry.Score];

            highestCount = Math.max(highestCount, ++matchingHistogram.count);
        });

        // Calculate percentages
        scoreHistogram.forEach(h => {
            h.percentage = h.count * 100 / highestCount;
        });

        return scoreHistogram;
    }

    getColor(rating: number) {
        if (rating >= 9) {
            return this.colourService.greenColor;
        }

        if (rating >= 7) {
            return this.colourService.orangeColor;
        }

        return this.colourService.redColor;
    }

    calculateAggregatedScoresByCustomer(entries: INPSEntry[]): INPSAggregatedScore[] {
        var customers: Map<number, INPSAggregatedScore> = new Map();

        entries.forEach(entry => {
            var customer = customers.get(Number(entry.CustomerId));

            if (!customer) {
                customer = {
                    description: entry.customer.CompanyName,
                    ratings: [],
                    score: {}
                };

                customers.set(Number(entry.CustomerId), customer);
            }

            customer.ratings.push(entry);
        });

        // Now calculate scores
        customers.forEach(customer => {
            customer.score = this.calculateNPSScore(customer.ratings);
        });

        return Array.from(customers.values());
    }

    calculateAggregatedScoresByReseller(entries: INPSEntry[]): INPSAggregatedScore[] {
        var resellers: Map<number, INPSAggregatedScore> = new Map();

        entries.forEach(entry => {
            var reseller = resellers.get(Number(entry.customer.ResellerId));

            if (!reseller) {
                reseller = {
                    description: entry.customer.resellerName,
                    ratings: [],
                    score: {}
                };

                resellers.set(Number(entry.customer.ResellerId), reseller);
            }

            reseller.ratings.push(entry);
        });

        // Now calculate scores
        resellers.forEach(reseller => {
            reseller.score = this.calculateNPSScore(reseller.ratings);
        });

        return Array.from(resellers.values());
    }

    // ---

    private createHttpParams(options?: IGetRatingsOptions): HttpParams {
        let params = new HttpParams();

        if (!options) {
            return params;
        }

        if (options.platformId) {
            params = params.set("platformId", options.platformId);
        }

        if (options.customerId) {
            params = params.set("customerId", options.customerId);
        }

        if (options.userId) {
            params = params.set("userId", options.userId);
        }

        if (options.start) {
            params = params.set("start", options.start?.toISOString());
        }

        if (options.end) {
            params = params.set("end", options.end?.toISOString());
        }

        return params;
    }

    private createCalculatedMetrics(count: number, totalEntries: number, color: string): INPSCalculatedMetrics {
        var metrics = {
            count: count,
            percentage: /*Math.round*/(count * 100 / totalEntries),
            color: color
        }

        return metrics;
    }
}
